import { CATALOG_SORTS, DEFAULT_CATALOG_SORT } from '@game-keys/shared'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { BackToTop } from '../components/BackToTop'
import { SoundToggle } from '../components/SoundToggle'
import { SHORTCUT_HINT } from '../lib/shortcut'
import { cue } from '../lib/sound'
import { CapsuleGrid, CapsuleGridSkeleton } from '../components/CapsuleGrid'
import { FilterRail, countActiveFilters } from '../components/FilterRail'
import { FilterSheet, FilterSheetButton } from '../components/FilterSheet'
import { ShelfRow } from '../components/ShelfRow'
import {
  parseList,
  toggleInList,
  useCatalogPages,
  useConfig,
  useStoreMap,
  type CatalogParams,
} from '../lib/queries'
import { useDebouncedValue } from '../lib/use-debounced-value'
import { useInView } from '../lib/use-in-view'
import { useScrollMemory } from '../lib/use-scroll-memory'
import { Select } from '../components/Select'

/** A whole number inside a range, or nothing. */
function boundedInt(value: unknown, min: number, max: number): number | undefined {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) return undefined

  return parsed
}

/**
 * Every filter lives in the URL, so any view of the catalog is a link that
 * can be pasted to someone and restores exactly what was on screen.
 *
 * The sort is checked against the shared list rather than a copy of it. A
 * hand-maintained copy is how two new sorts once shipped that the dropdown
 * offered and this quietly threw away — the URL never took them, so choosing
 * one appeared to do nothing at all.
 */
export const Route = createFileRoute('/')({
  validateSearch: (search: Record<string, unknown>): CatalogParams => ({
    q: typeof search.q === 'string' && search.q ? search.q : undefined,
    stores: typeof search.stores === 'string' ? search.stores : undefined,
    genres: typeof search.genres === 'string' ? search.genres : undefined,
    platforms: typeof search.platforms === 'string' ? search.platforms : undefined,
    unlock: typeof search.unlock === 'string' ? search.unlock : undefined,
    from: boundedInt(search.from, 1970, 2100),
    to: boundedInt(search.to, 1970, 2100),
    minScore: boundedInt(search.minScore, 0, 100),
    filter: search.filter === 'expiring' ? 'expiring' : undefined,
    // The default stays out of the URL entirely, so the plain address is the
    // default view rather than one carrying a redundant parameter.
    sort:
      typeof search.sort === 'string' &&
      search.sort !== DEFAULT_CATALOG_SORT &&
      (CATALOG_SORTS as readonly string[]).includes(search.sort)
        ? (search.sort as CatalogParams['sort'])
        : undefined,
  }),
  component: CatalogPage,
})

// Ordered as offered, with the default first.
const SORTS = [
  { value: 'score', label: 'Best reviewed' },
  { value: 'recent', label: 'Recently added' },
  { value: 'released', label: 'Newest release' },
  { value: 'title', label: 'A–Z' },
  { value: 'copies', label: 'Most copies' },
] as const

function SortIcon() {
  return (
    <svg aria-hidden viewBox="0 0 14 14" className="size-3.5">
      <path
        d="M2 3.5h10M3.5 7h7M5.5 10.5h3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

const DAY_MS = 24 * 60 * 60 * 1000

function daysUntil(iso: string | null): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  return Number.isNaN(ms) ? null : Math.ceil(ms / DAY_MS)
}

function CatalogPage() {
  const params = Route.useSearch()
  const navigate = useNavigate({ from: '/' })
  const config = useConfig()
  const storeMap = useStoreMap()

  // The input is local and the URL follows it, rather than the other way
  // round — typing must never feel like it is waiting on navigation.
  const [queryInput, setQueryInput] = useState(params.q ?? '')
  const debouncedQuery = useDebouncedValue(queryInput, 250)

  useEffect(() => {
    const next = debouncedQuery.trim() || undefined
    if (next === params.q) return

    navigate({ search: (prev) => ({ ...prev, q: next }), replace: true })
  }, [debouncedQuery, params.q, navigate])

  const catalog = useCatalogPages(params)
  const games = catalog.data?.pages.flatMap((page) => page.games) ?? []
  const total = catalog.data?.pages[0]?.total ?? 0

  // Loads the next page as the end of the grid comes into view.
  const { ref: sentinelRef, inView } = useInView<HTMLDivElement>()

  // Depend on the individual values rather than the query object, which is a
  // new reference on every render and would re-run this constantly.
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = catalog

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const isFiltered =
    Boolean(params.q) ||
    parseList(params.stores).length > 0 ||
    parseList(params.genres).length > 0 ||
    parseList(params.platforms).length > 0 ||
    parseList(params.unlock).length > 0 ||
    params.filter === 'expiring'

  const [filtersOpen, setFiltersOpen] = useState(false)

  // Keyed on the filters, so changing them starts at the top of what is now a
  // different list, and gated on having games so the restore has a page to
  // land in.
  useScrollMemory(JSON.stringify(params), games.length > 0)

  const setParam = (patch: Partial<CatalogParams>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }) })

  // The cue comes from the checkbox itself now, so no facet can be silent.
  const toggleFacet = (key: 'stores' | 'genres' | 'platforms' | 'unlock', slug: string) =>
    setParam({ [key]: toggleInList(params[key], slug) } as Partial<CatalogParams>)

  const clearFilters = () => {
    cue('dismissed')
    setQueryInput('')
    navigate({ search: {} })
  }

  const expiring = games.filter((game) => {
    const days = daysUntil(game.soonestExpiry)
    return days !== null && days <= 30
  })

  return (
    <div className="grain min-h-dvh">
      <header className="aurora relative border-b border-line">
        <div className="mx-auto w-full max-w-[1600px] px-4 pt-6 pb-5 sm:px-8 sm:pt-14 sm:pb-8">
          {/* Sound rides on the strapline rather than the title. Beside the
              title it was taking a fixed forty pixels out of the one line that
              needs them most, and on a narrow phone that is the difference
              between the site's name and "Korn's Key Emp…". This line is short
              at every width and has the room to spare. */}
          <div className="flex items-center gap-3">
            <p className="font-mono text-[10px] tracking-[0.22em] text-accent uppercase sm:text-[11px]">
              Spare keys · free to a good home
            </p>

            <SoundToggle className="ml-auto size-9 shrink-0 rounded-lg border border-line bg-surface-1/80 sm:size-10" />
          </div>

          <h1 className="mt-2 flex items-center gap-3 font-display text-3xl font-semibold tracking-[-0.03em] sm:mt-4 sm:gap-4 sm:text-6xl">
            {/* The same mark as the browser tab, so the page and the tab it is
                in are recognisably the same thing. */}
            <img
              src="/favicon.svg"
              alt=""
              className="size-8 shrink-0 rounded-lg bg-[color-mix(in_oklab,var(--color-accent)_16%,var(--color-surface-0))] p-1 sm:size-14 sm:rounded-xl sm:p-1.5"
            />
            {/*
              Wraps rather than truncates, and has the whole line to do it in.
              A site is entitled to its own name: cutting it off is the one
              thing this heading must not do, and a second line costs far less
              than an ellipsis.

              No fallback either — a placeholder here would flash a name that is
              not this site's while the config loads.
            */}
            <span className="min-w-0">{config.data?.siteName}</span>
          </h1>

          <p className="mt-2 max-w-prose text-[13px] leading-relaxed text-ink-soft sm:mt-4 sm:text-sm">
            {config.data?.bannerMessage ??
              'Anything here is going spare. Find something you want and ask — no accounts, no catches.'}
          </p>

          <div className="mt-4 flex flex-wrap items-center gap-2 sm:mt-8 sm:gap-3">
            {/* The hint is a sibling rather than part of the placeholder: it
                has to disappear on a phone, where the shortcut cannot be
                typed, and a placeholder is one string that cannot be styled
                or hidden in pieces. */}
            <div className="relative w-full sm:max-w-sm">
              <input
                type="search"
                value={queryInput}
                onChange={(event) => setQueryInput(event.target.value)}
                placeholder="Search the library…"
                aria-label="Search the library"
                className="w-full rounded-xl border border-line bg-surface-1/80 px-4 py-2.5 text-sm text-ink outline-none backdrop-blur transition-colors placeholder:text-ink-muted focus:border-accent"
              />

              <kbd
                aria-hidden
                className="kbd-hint pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-ink-muted"
              >
                {SHORTCUT_HINT}
              </kbd>
            </div>

            <Select
              value={params.sort ?? DEFAULT_CATALOG_SORT}
              // The cue comes from Select itself now, so every dropdown has one.
              onChange={(value) => setParam({ sort: value as CatalogParams['sort'] })}
              options={SORTS.map((option) => ({ value: option.value, label: option.label }))}
              label="Sort by"
              size="lg"
              icon={<SortIcon />}
              className="flex-1 basis-40 sm:w-52 sm:flex-none"
            />

            {/* A switch rather than a button: it is a state you are in, not an
                action you take, and the two read very differently. */}
            <button
              type="button"
              onClick={() => {
                cue('toggled')
                setParam({ filter: params.filter === 'expiring' ? undefined : 'expiring' })
              }}
              role="switch"
              aria-checked={params.filter === 'expiring'}
              className={`hidden shrink-0 items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm whitespace-nowrap transition-colors lg:flex lg:px-3.5 ${
                params.filter === 'expiring'
                  ? 'border-warn/50 bg-warn/15 text-warn'
                  : 'border-line bg-surface-1/80 text-ink-soft hover:text-ink'
              }`}
            >
              <span
                aria-hidden
                className={`relative h-4 w-7 rounded-full transition-colors ${
                  params.filter === 'expiring' ? 'bg-warn/70' : 'bg-surface-4'
                }`}
              >
                <span
                  className={`absolute top-0.5 size-3 rounded-full bg-ink transition-[left] duration-200 ${
                    params.filter === 'expiring' ? 'left-3.5' : 'left-0.5'
                  }`}
                />
              </span>
              <span className="sm:hidden">Expiring</span>
              <span className="hidden sm:inline">Expiring soon</span>
            </button>

            <FilterSheetButton
              activeCount={countActiveFilters(params)}
              onOpen={() => setFiltersOpen(true)}
            />
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-8 sm:py-8">
        <div className="flex flex-col gap-8 lg:flex-row">
          <FilterRail
            params={params}
            onToggle={toggleFacet}
            onSet={setParam}
            onClear={clearFilters}
          />

          <div className="min-w-0 flex-1">
            {catalog.isError ? (
              <p className="rounded-lg border border-bad/30 bg-bad/10 px-4 py-3 text-sm text-bad">
                Could not load the catalog. {(catalog.error as Error).message}
              </p>
            ) : catalog.isPending ? (
              <CapsuleGridSkeleton />
            ) : games.length === 0 ? (
              <EmptyState filtered={isFiltered} onClear={clearFilters} />
            ) : (
              <div className="space-y-10">
                {!isFiltered && expiring.length > 0 ? (
                  <ShelfRow
                    title="Expiring soon"
                    hint="claim these first"
                    games={expiring}
                    storeMap={storeMap}
                  />
                ) : null}

                <section>
                  <div className="mb-4 flex items-baseline gap-3">
                    <h2 className="font-display text-lg font-semibold tracking-tight">
                      {isFiltered ? 'Matches' : 'Everything'}
                    </h2>
                    <span className="font-mono text-xs text-ink-muted tabular-nums">
                      {total}
                    </span>
                  </div>

                  <CapsuleGrid
                    games={games}
                    storeMap={storeMap}
                  />

                  {/*
                    Scroll sentinel. The button is not a fallback for show —
                    an observer that never fires (reduced motion, an odd
                    browser, a very tall window) would otherwise strand the
                    rest of the library out of reach.
                  */}
                  <div ref={sentinelRef} className="mt-8 flex justify-center">
                    {catalog.isFetchingNextPage ? (
                      <p className="font-mono text-xs text-ink-muted">loading more…</p>
                    ) : catalog.hasNextPage ? (
                      <button
                        type="button"
                        onClick={() => catalog.fetchNextPage()}
                        className="rounded-lg border border-line px-4 py-2 text-sm text-ink-soft transition-colors hover:border-line-strong hover:text-ink"
                      >
                        Load more
                      </button>
                    ) : games.length > 24 ? (
                      <p className="font-mono text-xs text-ink-muted">
                        that's all {total} of them
                      </p>
                    ) : null}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </main>

      <BackToTop />

      <FilterSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        activeCount={countActiveFilters(params)}
      >
        <FilterRail
          params={params}
          onToggle={toggleFacet}
          onSet={setParam}
          onClear={clearFilters}
          headless
        />
      </FilterSheet>
    </div>
  )
}

function EmptyState({ filtered, onClear }: { filtered: boolean; onClear: () => void }) {
  return (
    <div className="rounded-xl border border-dashed border-line-strong px-6 py-20 text-center">
      <h2 className="font-display text-xl font-semibold">
        {filtered ? 'Nothing matches those filters' : 'Nothing here yet'}
      </h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-ink-soft">
        {filtered
          ? 'Try removing a filter or searching for something else.'
          : 'Once keys are added they will show up here. Check back soon.'}
      </p>
      {filtered ? (
        <button
          type="button"
          onClick={onClear}
          className="mt-5 rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-surface-0 transition-colors hover:bg-accent-strong"
        >
          Clear filters
        </button>
      ) : null}
    </div>
  )
}
